import { Component, Element, Event, EventEmitter, forceUpdate, Fragment, h, Listen, Prop } from '@stencil/core';
import Bind from 'bind-decorator';
import { BreakPointDown, BreakPointUp, LinkTarget, ZdsColors } from '../../types';
import { whenActuallyActivated } from '../../utils/accessibility';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { generateUniqueUuid } from '../../utils/generate-unique-uuid';
import { createMutationObserver } from '../../utils/mutation-observer';
import { sizeStringToCss } from '../../utils/size-string-to-css';
import { hasSlotted } from '../../utils/slotted';

@Component({
	shadow: true,
	styleUrl: 'zds-card.scss',
	tag: 'zds-card',
})
export class ZdsCard {
	containerRatio: string;
	mediaContainerRatio: string;
	mutationObserver: MutationObserver;

	@Element() hostElement: HTMLDivElement;

	@Event({ bubbles: true }) zdsClick: EventEmitter<MouseEvent>;

	@Prop() alignment: 'start' | 'center' | 'end' = 'start';
	@Prop() clickable: boolean;
	@Prop({ reflect: true }) color: ZdsColors;
	@Prop() footerHeight: string;
	@Prop({ reflect: true }) horizontal: BreakPointUp | BreakPointDown | boolean;
	@Prop() link: string;
	@Prop() linkTarget: LinkTarget = '_self';
	@Prop() mediaFit: 'cover' | 'contain' = 'cover';
	@Prop({ reflect: true }) mirror: boolean;
	@Prop() noBodyInset: boolean;
	@Prop({ reflect: true }) ratio: string;
	@Prop() linkArea: 'card' | 'media' = 'card';

	@Bind
	onClick(event: MouseEvent) {
		event.stopPropagation();

		if (this.linkArea === 'card') {
			this.zdsClick.emit(event);
		}
	}

	@Bind
	onMediaClick(event: MouseEvent) {
		event.stopPropagation();
		this.zdsClick.emit(event);
	}

	@Listen('keydown')
	onKeyDown(event: KeyboardEvent) {
		if (!this.clickable) {
			return;
		}

		whenActuallyActivated(event, this.zdsClick.emit);
	}

	componentWillLoad() {
		this.syncRatios();

		this.mutationObserver = createMutationObserver(this.hostElement, this.handleMutation, {
			childList: true,
		});
	}

	disconnectedCallback() {
		this.mutationObserver?.disconnect();
	}

	render() {
		let horizontalVerticalClass: string;
		if (this.horizontal === true || (this.horizontal as string) === 'true' || (this.horizontal as string) === '') {
			horizontalVerticalClass = 'horizontal';
		} else if (this.horizontal === undefined || (this.horizontal as string) === 'false') {
			horizontalVerticalClass = 'vertical';
		} else {
			horizontalVerticalClass = `horizontal-${this.horizontal}`;
		}

		const cardClasses = generateCssClasses('zds-card', {
			[`alignment-${this.alignment}`]: !!this.alignment,
			hasMedia: !!hasSlotted(this.hostElement, 'media'),
			[horizontalVerticalClass]: true,
			hasRatio: !!this.ratio,
			mirror: this.mirror && this.horizontal !== undefined,
			noBodyInset: this.noBodyInset,
		});

		const ComponentTag = (this.link || this.clickable) && this.linkArea === 'card' ? 'a' : 'div';

		let linkAttributes = {};

		if (this.linkArea === 'card') {
			if (this.link) {
				linkAttributes = {
					href: this.link,
					tabIndex: 0,
					target: this.linkTarget,
				};
			} else if (this.clickable) {
				linkAttributes = {
					tabIndex: 0,
				};
			}
		}

		return (
			<ComponentTag class={cardClasses} onClick={this.onClick} {...linkAttributes}>
				<div
					class="zds-card__media-container"
					style={this.mediaContainerRatio && { flex: this.mediaContainerRatio }}
				>
					{this.renderMedia()}
				</div>
				<div class="zds-card__container" style={this.containerRatio && { flex: this.containerRatio }}>
					<div class="zds-card__main">
						{this.renderIcon()}
						{this.renderHeader()}
						{this.renderBody()}
					</div>
					{this.renderFooter()}
				</div>
			</ComponentTag>
		);
	}

	renderMedia() {
		if (!hasSlotted(this.hostElement, 'media')) {
			return null;
		}

		const mediaClasses = generateCssClasses('zds-card__media', {
			[`fit-${this.mediaFit}`]: true,
		});

		const mediaContent = (
			<Fragment>
				<div class={mediaClasses}>
					<slot name="media" />
				</div>
				<div class="zds-card__media-overlay">
					<div class="zds-card__media-overlay__content">
						<slot name="media-overlay" />
					</div>
					<div class="zds-card__status">
						<slot name="status"></slot>
					</div>
				</div>
			</Fragment>
		);

		if (this.linkArea === 'media') {
			return (
				<a
					class="zds-card__media-link"
					onClick={this.onMediaClick}
					target={this.linkTarget}
					href={this.link}
					tabIndex={0}
				>
					{mediaContent}
				</a>
			);
		} else {
			return mediaContent;
		}
	}

	renderIcon() {
		if (!hasSlotted(this.hostElement, 'icon')) {
			return null;
		}
		return (
			<div class="zds-card__icon">
				<slot name="icon" />
			</div>
		);
	}

	renderHeader() {
		const headerSlots = ['preheader', 'header', 'subheader'];

		const hasHeaderSlot = headerSlots.some((slot) => hasSlotted(this.hostElement, slot));
		if (!hasHeaderSlot) return null;

		return (
			<div class="zds-card__header">
				<div class="zds-card__header-wrapper">
					<div class="zds-card__header-start">
						<slot name="header-start"></slot>
					</div>
					<div class="zds-card__header-main">
						{headerSlots.map((slot) => (
							<slot key={generateUniqueUuid(slot)} name={slot}></slot>
						))}
					</div>
					<div class="zds-card__header-end">
						<slot name="header-end"></slot>
					</div>
				</div>
			</div>
		);
	}

	renderBody() {
		if (!hasSlotted(this.hostElement, 'body')) {
			return null;
		}

		return (
			<div class="zds-card__body">
				<slot name="body"></slot>
			</div>
		);
	}

	renderFooter() {
		if (!hasSlotted(this.hostElement, 'footer')) {
			return null;
		}

		const footerClasses = this.footerHeight
			? generateCssClasses('zds-card__footer', { customHeight: true })
			: 'zds-card__footer';

		const footerStyle = this.footerHeight ? { height: sizeStringToCss(this.footerHeight) } : undefined;

		return (
			<div class={footerClasses} style={footerStyle}>
				<slot name="footer"></slot>
			</div>
		);
	}

	/**
	 * Synchronizes media and container ratios based on ratio given.
	 * If ratio is not given or in an invalid format, deletes ratio and logs warning.
	 */
	syncRatios() {
		if (!this.ratio) return;

		const ratioRegex = /^\d+\/\d+$/;
		if (!ratioRegex.test(this.ratio)) {
			delete this.ratio;
			console.warn(`zds-card: could not utilize given ratio ${this.ratio}, expected format number/number`);
			return;
		}

		[this.mediaContainerRatio, this.containerRatio] = this.ratio.split('/');
	}

	/**
	 * Force a re-render of the component when content changes.
	 * This is especially needed when a slotted node gets dynamically added/removed.
	 */
	@Bind
	handleMutation() {
		forceUpdate(this.hostElement);
	}
}
